/**
 * Access-token identity for API calls.
 *
 * localStorage `authToken` is not trustworthy on its own: a leftover Auth0
 * SPA cache (`@@auth0spajs@@*`) or an in-flight `getAccessTokenSilently`
 * can rewrite it after email signup has already stored a new JWT. The pin
 * is the session that `storeTokens` / `adoptSessionTokens` just established
 * and is what fetchService must send.
 */

import { parseJwtPayload } from './jwtUtils';

const AUTH0_SPA_CACHE_PREFIX = '@@auth0spajs@@';

let pinnedAccessToken: string | null = null;
let pinnedIdToken: string | null = null;
let pinnedRefreshToken: string | null = null;

function readStoredAccessToken(): string | null {
  try {
    return localStorage.getItem('authToken') || localStorage.getItem('id_token');
  } catch {
    return null;
  }
}

/** The access token API calls must send — pinned session first, then storage. */
export function getPinnedOrStoredAccessToken(): string | null {
  return pinnedAccessToken || readStoredAccessToken();
}

export function getPinnedAccessToken(): string | null {
  return pinnedAccessToken;
}

export function pinAccessToken(token: string | null): void {
  pinnedAccessToken = token;
  if (!token) return;
  try {
    localStorage.setItem('authToken', token);
  } catch {
    /* ignore */
  }
}

/**
 * Pin the tokens from a completed login/signup and write them to storage.
 * Also drops leftover Auth0 SPA cache keys so silent refresh cannot resurrect
 * a previous identity.
 */
export function pinSessionTokens(tokens: {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
}): void {
  wipeAuth0SpaCache();
  if (tokens.access_token) {
    pinAccessToken(tokens.access_token);
  }
  if (tokens.id_token) {
    pinnedIdToken = tokens.id_token;
    try {
      localStorage.setItem('id_token', tokens.id_token);
    } catch {
      /* ignore */
    }
  }
  if (tokens.refresh_token) {
    pinnedRefreshToken = tokens.refresh_token;
    try {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    } catch {
      /* ignore */
    }
  }
  reassertPinnedSessionTokens();
}

/** Restore pinned tokens if leftover Auth0 / SDK code overwrote localStorage. */
export function reassertPinnedSessionTokens(): void {
  if (!pinnedAccessToken && !pinnedIdToken && !pinnedRefreshToken) return;
  try {
    if (pinnedAccessToken && localStorage.getItem('authToken') !== pinnedAccessToken) {
      let lsSub: string | null = null;
      let pinSub: string | null = null;
      try {
        lsSub = parseJwtPayload(localStorage.getItem('authToken') || '')?.sub ?? null;
        pinSub = parseJwtPayload(pinnedAccessToken)?.sub ?? null;
      } catch {
        /* ignore */
      }
      console.warn('[AUTH_DEBUG] leftover overwrite of localStorage.authToken — restoring pin', {
        lsSub,
        pinSub,
      });
      localStorage.setItem('authToken', pinnedAccessToken);
    }
    if (pinnedIdToken && localStorage.getItem('id_token') !== pinnedIdToken) {
      localStorage.setItem('id_token', pinnedIdToken);
    }
    if (pinnedRefreshToken && localStorage.getItem('refresh_token') !== pinnedRefreshToken) {
      localStorage.setItem('refresh_token', pinnedRefreshToken);
    }
  } catch {
    /* ignore */
  }
}

/** Drop the pin on logout so the next identity cannot inherit it. */
export function clearPinnedSessionTokens(): void {
  pinnedAccessToken = null;
  pinnedIdToken = null;
  pinnedRefreshToken = null;
}

export function wipeAuth0SpaCache(): void {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(AUTH0_SPA_CACHE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/**
 * True when `token` is still the access token this session should use.
 *
 * Late 401s (stale existing-token checks) and Auth0 silent-auth errors must
 * not tear down a session that already replaced this token — e.g. email
 * signup writing a new JWT while an expired leftover token's profile
 * request is still in flight.
 */
export function isCurrentAccessToken(token: string | null | undefined): boolean {
  return (token ?? null) === (getPinnedOrStoredAccessToken() ?? null);
}
